import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  IVacationChangeRequestDecision,
  IVacationChangeRequestsResponse,
  IVacationChangeWindowPayload,
} from "@vmosolutions/oc-interfaces";

import {
  createVacationChangeWindow,
  decideVacationChangeRequest,
  deleteVacationChangeWindow,
  getVacationChangeRequests,
  shareVacationChangeWindow,
  updateVacationChangeWindow,
} from "../helpers";
import { createSerialQueue } from "../Awarding/awardingHelpers";
import { applyDecisionResponse } from "./changeRequestHelpers";

/** A window write returns the whole view, which replaces the one on screen. */
const replaceView = (
  _current: IVacationChangeRequestsResponse,
  response: IVacationChangeRequestsResponse
): IVacationChangeRequestsResponse => response;

export interface IVacationChangeRequests {
  /** Null until the first load for this package lands. */
  view: IVacationChangeRequestsResponse | null;
  loading: boolean;
  /** True while any write is queued or running. */
  busy: boolean;
  load: () => Promise<void>;
  createWindow: (payload: IVacationChangeWindowPayload) => Promise<boolean>;
  updateWindow: (windowId: string, payload: IVacationChangeWindowPayload) => Promise<boolean>;
  deleteWindow: (windowId: string) => Promise<boolean>;
  shareWindow: (windowId: string) => Promise<boolean>;
  decide: (requestId: string, decision: IVacationChangeRequestDecision) => Promise<boolean>;
}

/**
 * The Manage Awards view for one package. Writes are sent one at a time in the order they were
 * made, and a refused write reloads the view so it shows what the server holds.
 */
export const useVacationChangeRequests = (packageId: string | undefined): IVacationChangeRequests => {
  const dispatch = useDispatch();

  const [view, setView] = useState<IVacationChangeRequestsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [writes, setWrites] = useState<number>(0);

  // A response for a package the editor has since moved off must not land in the new one's view.
  const packageRef = useRef<string | undefined>(packageId);
  packageRef.current = packageId;

  const [syncedPackageId, setSyncedPackageId] = useState<string | undefined>(packageId);
  if (packageId !== syncedPackageId) {
    setSyncedPackageId(packageId);
    setView(null);
  }

  const queueRef = useRef<ReturnType<typeof createSerialQueue> | null>(null);
  if (!queueRef.current) queueRef.current = createSerialQueue();
  const enqueue = queueRef.current;
  // Counts landed writes, so a load that was read before one of them landed is not applied over it.
  const writesLandedRef = useRef<number>(0);
  // Counts loads started. Refresh and the window boundary's reload can overlap, so only the newest may land or stop the
  // spinner.
  const loadsStartedRef = useRef<number>(0);

  const load = useCallback(async (): Promise<void> => {
    if (!packageId) return;
    const loadNumber = (loadsStartedRef.current += 1);
    const writesLandedAtStart = writesLandedRef.current;
    setLoading(true);
    try {
      const response = await getVacationChangeRequests(packageId, {
        dispatch,
        errorMessage: "Unable to load the package's change requests",
      });
      if (
        response &&
        loadsStartedRef.current === loadNumber &&
        packageRef.current === packageId &&
        writesLandedRef.current === writesLandedAtStart
      ) {
        setView(response);
      }
    } finally {
      if (loadsStartedRef.current === loadNumber) setLoading(false);
    }
  }, [dispatch, packageId]);

  /** Queues a write, and applies what it returns to the view. */
  const write = useCallback(
    <T>(
      send: (pkgId: string) => Promise<T | null>,
      apply: (current: IVacationChangeRequestsResponse, response: T) => IVacationChangeRequestsResponse
    ): Promise<boolean> => {
      if (!packageId) return Promise.resolve(false);
      setWrites((count) => count + 1);
      return enqueue(async (): Promise<boolean> => {
        try {
          const response = await send(packageId);
          if (packageRef.current !== packageId) return false;
          if (!response) {
            void load();
            return false;
          }
          writesLandedRef.current += 1;
          setView((current) => (current ? apply(current, response) : current));
          return true;
        } catch {
          return false;
        } finally {
          setWrites((count) => count - 1);
        }
      });
    },
    [packageId, enqueue, load]
  );

  const createWindow = useCallback(
    (payload: IVacationChangeWindowPayload) =>
      write(
        (pkgId) =>
          createVacationChangeWindow(pkgId, payload, {
            dispatch,
            successMessage: "Change window saved as a draft",
            errorMessage: "Unable to create the change window",
          }),
        replaceView
      ),
    [dispatch, write]
  );

  const updateWindow = useCallback(
    (windowId: string, payload: IVacationChangeWindowPayload) =>
      write(
        (pkgId) =>
          updateVacationChangeWindow(pkgId, windowId, payload, {
            dispatch,
            successMessage: "Change window saved",
            errorMessage: "Unable to save the change window",
          }),
        replaceView
      ),
    [dispatch, write]
  );

  const deleteWindow = useCallback(
    (windowId: string) =>
      write(
        (pkgId) =>
          deleteVacationChangeWindow(pkgId, windowId, {
            dispatch,
            successMessage: "Change window deleted",
            errorMessage: "Unable to delete the change window",
          }),
        replaceView
      ),
    [dispatch, write]
  );

  const shareWindow = useCallback(
    (windowId: string) =>
      write(
        (pkgId) =>
          shareVacationChangeWindow(pkgId, windowId, {
            dispatch,
            successMessage: "Change window shared to crew",
            errorMessage: "Unable to share the change window",
          }),
        replaceView
      ),
    [dispatch, write]
  );

  const decide = useCallback(
    (requestId: string, decision: IVacationChangeRequestDecision) =>
      write(
        (pkgId) =>
          decideVacationChangeRequest(pkgId, requestId, decision, {
            dispatch,
            errorMessage: "Unable to decide the change request",
          }),
        applyDecisionResponse
      ),
    [dispatch, write]
  );

  return {
    view,
    loading,
    busy: writes > 0,
    load,
    createWindow,
    updateWindow,
    deleteWindow,
    shareWindow,
    decide,
  };
};
